import { type ReactElement, type ReactNode, useState } from "react"
import styled from "styled-components"

export type SectionSize = "md" | "lg"

/**
 * `lg` also carries extra padding below its content, because the layout that
 * asks for it is the rail — whose horizontal scrollbar would otherwise sit on
 * the section's own edge. A grid has no scrollbar, so `md` would leave that as
 * dead space.
 */
const Root = styled.section<{ $size: SectionSize }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme, $size }) =>
    $size === "lg" ? `${theme.spacing.xl} ${theme.spacing.xl} ${theme.spacing.xxl}` : theme.spacing.lg};
  border-radius: ${({ theme }) => theme.radius.lg};
  background-color: ${({ theme }) => theme.color.surface};
`

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`

const headingText = `
  display: flex;
  align-items: baseline;
`

const Heading = styled.h2<{ $size: SectionSize }>`
  ${headingText}
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme, $size }) => ($size === "lg" ? theme.fontSize.lg : theme.fontSize.md)};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  color: ${({ theme }) => theme.color.textPrimary};
`

/** The whole heading is the control, so the target is the words rather than a chevron beside them. */
const Disclosure = styled.button`
  ${headingText}
  gap: ${({ theme }) => theme.spacing.xs};
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: left;
`

/** Rotates rather than swapping glyphs, so the open and closed states are the same mark. */
const Chevron = styled.span<{ $isOpen: boolean }>`
  align-self: center;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textSecondary};
  transform: rotate(${({ $isOpen }) => ($isOpen ? "90deg" : "0deg")});
`

/**
 * Repeats `Root`'s column so a collapsible section's children keep the spacing
 * they would have had as its direct children. The `[hidden]` guard is not
 * redundant: `display: flex` here outranks the user agent's `[hidden]` rule.
 */
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};

  &[hidden] {
    display: none;
  }
`

const Count = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  color: ${({ theme }) => theme.color.textMuted};
`

interface SectionProps {
  readonly title: string
  /** `lg` frames a section bigger, for a layout meant to be presented rather than surveyed. */
  readonly size?: SectionSize
  /** Rendered beside the heading. Omitted rather than zero when there is nothing to count. */
  readonly count?: number
  /**
   * Turns the heading into a disclosure, closed to begin with. Deliberately not
   * persisted: a section worth hiding by default is one that should be hidden
   * again next time the page opens.
   */
  readonly collapsible?: boolean
  readonly action?: ReactNode
  readonly children: ReactNode
  readonly qaHook: string
}

export const Section = ({
  title,
  count,
  action,
  size = "md",
  collapsible = false,
  children,
  qaHook,
}: SectionProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(false)
  const bodyId = `${qaHook}-body`

  /** Unmounted rather than hidden, so a collapsed section's rows stop fetching. */
  const body = !collapsible || isOpen ? children : null

  const heading = (
    <>
      {title}
      {count === undefined ? null : <Count data-qa-hook={`${qaHook}-count`}>{count}</Count>}
    </>
  )

  return (
    <Root $size={size} data-qa-hook={qaHook} aria-label={title}>
      <Header>
        <Heading $size={size}>
          {collapsible ? (
            <Disclosure
              type="button"
              aria-expanded={isOpen}
              aria-controls={bodyId}
              data-qa-hook={`${qaHook}-disclosure`}
              onClick={() => setIsOpen(open => !open)}
            >
              <Chevron $isOpen={isOpen} aria-hidden="true">
                ▶
              </Chevron>
              {heading}
            </Disclosure>
          ) : (
            heading
          )}
        </Heading>
        {action}
      </Header>
      {collapsible ? (
        <Body id={bodyId} hidden={!isOpen}>
          {body}
        </Body>
      ) : (
        body
      )}
    </Root>
  )
}
